import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  getCourseGroupList,
  getCourseBannerList,
  getCourseHomeFilterInfo,
  getTabCourseList,
} from 'reduxModules/course/selector';
import useStyles from 'hooks/useStyles';
import { buildQuery } from 'utils/query';
import ToTop from 'components/ToTop';
import PromotionBanner from 'components/PromotionBanner';
import LevelSelector from 'components/LevelSelector';
import Testimonial from 'components/Testimonial';
import Features from 'components/Features';
import { EMPTY_STRING } from 'constants/i18n';

import CourseListByGroup from './components/CourseListByGroup';
import Layout from './components/Layout';
import GroupLinkList from './components/GroupLinkList';
import InfiniteHorizontalScrollContainer from './components/InfiniteVerticalScrollContainer';
import useGetDispatchActions from './useGetDispatchActions';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './CourseHome.scss';

const PER_PAGE = 3;

const getCurrentPageGroupIdList = (groupList, currentPage, noLimit) => {
  const getGroupIdList = list => list.map(({ key }) => key);

  if (noLimit) return getGroupIdList(groupList);

  const maxCount = currentPage * PER_PAGE - 1;
  const minCount = (currentPage - 1) * PER_PAGE - 1;

  return getGroupIdList(
    groupList.filter((item, index) => index > minCount && maxCount >= index),
  );
};

const FEATURE_ELEMENT_TYPES = {
  instructor: {
    desktopType: 1,
    mobileType: 1,
  },
  material: {
    desktopType: 2,
    mobileType: 1,
  },
  outline: {
    desktopType: 3,
    mobileType: 1,
  },
};

const getFeatureItem = ([category, { title, ...other }]) => {
  if (title === EMPTY_STRING) return null;

  const elementType = FEATURE_ELEMENT_TYPES[category];

  return {
    title,
    desktopType: 1,
    mobileType: 1,
    ...other,
    ...elementType,
  };
};

export default function CourseHome() {
  useStyles(styles);

  const {
    dispatchFetchBanners,
    dispatchFetchCourseList,
    dispatchUpdateCurrentPage,
    dispatchClearCourseList,
    dispatchUpdateSelectedGradeLevel,
    dispatchFetchGradeLevels,
    dispatchFetchGroupList,
    dispatchBannerClickGTMEvent,
  } = useGetDispatchActions();

  const { t } = useTranslation(['course']);
  const bannerList = useSelector(getCourseBannerList);
  const groupList = useSelector(getCourseGroupList);
  const { gradeLevels, selectedGradeLevel, selectedSubject } = useSelector(
    getCourseHomeFilterInfo,
  );

  const courseList = useSelector(getTabCourseList);
  const { currentPage } = useSelector(getCourseHomeFilterInfo);
  const [currentGroupList, setCurrentGroupList] = useState([]);

  const noLimit = Object.keys(courseList).length === 0;

  const featuresConfig = useMemo(() => {
    const obj = t('home.courseIntro.feature', {
      returnObjects: true,
    });

    if (typeof obj !== 'object') return [];

    const { title, description, ...featureObj } = obj;

    return Object.entries(featureObj).reduce((total, item) => {
      const next = getFeatureItem(item);

      if (next === null) return total;

      return [...total, next];
    }, []);
  }, [t]);

  const updateCurrentGroupList = (totalGroupList, page) => {
    const nextGroupList = totalGroupList.filter(
      (item, index) => index + 1 <= page * PER_PAGE,
    );

    setCurrentGroupList(() => nextGroupList);
    return nextGroupList;
  };

  // eslint-disable-next-line no-shadow
  const updateNextCourseList = (totalGroupList, page, noLimit) => {
    const groupIdList = getCurrentPageGroupIdList(
      totalGroupList,
      page,
      noLimit,
    );

    if (groupIdList.length !== 0) {
      dispatchFetchCourseList(groupIdList);
    }
  };

  const handleBannerClick = ({ id, content }) => {
    dispatchBannerClickGTMEvent({
      bannerId: id,
      bannerTitle: content,
    });
  };

  const handleFilterChange = useCallback(
    ([selectingGradeLevel, selectingSubject]) => {
      const payload = {
        selectedGradeLevel: selectingGradeLevel,
        selectedSubject: selectingSubject,
      };
      if (selectingGradeLevel !== selectedGradeLevel) {
        buildQuery('gradeLevelId', selectingGradeLevel);
        buildQuery('subjectId', 0);
      } else if (selectingSubject !== selectedSubject) {
        buildQuery('subjectId', selectingSubject);
      }

      dispatchClearCourseList();
      dispatchUpdateSelectedGradeLevel(payload);
      dispatchFetchGroupList(payload);
      dispatchUpdateCurrentPage(0);
      setCurrentGroupList(() => []);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedGradeLevel, selectedSubject],
  );

  const onScrollBottomBounding = (type, isInView) => {
    if (!(type === 'bottom') || !isInView || currentPage === 0) return;

    const maxPage = Math.ceil(groupList.length / PER_PAGE);

    const nextPage = currentPage > maxPage ? currentPage : currentPage + 1;
    dispatchUpdateCurrentPage(nextPage);

    const nextGroupList = updateCurrentGroupList(groupList, nextPage);
    updateNextCourseList(nextGroupList, nextPage, noLimit);
  };

  useEffect(() => {
    dispatchFetchGradeLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatchClearCourseList();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatchFetchBanners({ gradeLevelId: selectedGradeLevel });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGradeLevel]);

  useEffect(() => {
    if (groupList.length !== 0 && currentGroupList.length === 0) {
      const nextPage = currentPage === 0 ? 1 : currentPage;
      dispatchUpdateCurrentPage(nextPage);

      const nextGroupList = updateCurrentGroupList(groupList, nextPage);
      updateNextCourseList(nextGroupList, nextPage, noLimit);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupList, currentPage, noLimit, currentGroupList]);

  return (
    <Layout>
      <h1>{t('home.courseIntro.title.desktop')}</h1>
      <div className={styles.subTitle}>{t('home.courseIntro.description')}</div>
      <div className={styles.bannerList}>
        <PromotionBanner
          id="courseHomeBanner"
          bannerList={bannerList}
          onClick={handleBannerClick}
        />
      </div>
      <div className={styles.groupList}>
        <GroupLinkList list={groupList} />
      </div>
      <div className={styles.gradeLevel}>
        <LevelSelector
          gradeLevelList={gradeLevels}
          value={[selectedGradeLevel, selectedSubject]}
          onChange={handleFilterChange}
        />
      </div>

      <InfiniteHorizontalScrollContainer
        className={styles.courseList}
        onScrollBottomBounding={onScrollBottomBounding}
      >
        {currentGroupList.map(({ key, title }) => (
          <CourseListByGroup
            key={key}
            groupId={key}
            groupTitle={title}
            courseInfo={courseList[key]}
            courseCardMoreText={t('home.courseIntro.courseCardMore')}
          />
        ))}
      </InfiniteHorizontalScrollContainer>

      <Testimonial
        className={styles.testimonial}
        id="course-testimonial"
        type="course"
      />
      <Features
        id="course_feature"
        title={t('home.courseIntro.feature.title')}
        description={t('home.courseIntro.feature.description')}
        featuresConfig={featuresConfig}
        onScrollHalf={() => {}}
        className={styles.feature}
        activeClassName={styles.activeFeature}
      />
      <ToTop text={t('home.courseIntro.backToTop')} />
    </Layout>
  );
}
